import { Fragment, useState } from "react";
import { Modal } from "react-bootstrap";
import Tooltip from "@mui/material/Tooltip";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBan, faEdit, faTruck } from "@fortawesome/free-solid-svg-icons";
import { TableLoader } from "../../../../Common/ShimmerLoader";
import { useDrainSlitComplaintReportStore } from "./useDrainSlitComplaintReportStore";

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
};

const HEADER_BG = "#004554";
const HEADER_TEXT = "White";
const BORDER = "#E0E0E0";
const EXPAND_BG = "#F1F5F9";
const ORANGE = "#004554";

const MAIN_COLUMNS = [
  "S.No",
  "Image",
  "Complaint No",
  "Complainant Contact No",
  "Complainant Name",
  "Ward",
  "Colony Name",
  "Description",
  "Latitude, Longitude",
  "Complaint Created On",
  "Complaint Closed On",
  "Status",
  "Actual Duration",
  "Close Time Location",
  "Closing Image",
  "Action Remarks",
  "Revised Ward",
  "Revised Colony",
  "Revised Location",
  "Closed By",
  "Actions",
];

const INNER_COLUMNS = [
  "S.No",
  "Assigned To",
  "Status",
  "Action Remarks",
  "Assigned On",
  "Action Taken On",
  "Total Time(Hr:Min)",
  "Assigned By",
];

const STATUS_STYLES = {
  closed: { color: "#0f5132", backgroundColor: "#d1e7dd" },
  opened: { color: "#084298", backgroundColor: "#cfe2ff" },
  invalid: { color: "#842029", backgroundColor: "#f8d7da" },
  default: { color: "#495057", backgroundColor: "#e9ecef" },
};

const thStyle = {
  position: "sticky",
  top: 0,
  zIndex: 10,
  whiteSpace: "nowrap",
  borderBottom: "1px solid #BFDBFE",
  backgroundColor: HEADER_BG,
  color: HEADER_TEXT,
  padding: "6px 8px",
  fontSize: "12px",
  fontWeight: 600,
  textAlign: "center",
  lineHeight: 1.2,
};

const tdStyle = {
  whiteSpace: "nowrap",
  border: `1px solid ${BORDER}`,
  padding: "4px 8px",
  textAlign: "center",
  color: "#374151",
  fontSize: "12px",
  backgroundColor: "#FFFFFF",
  lineHeight: 1.2,
};

const subThStyle = {
  ...thStyle,
  padding: "8px 10px",
  fontSize: "12px",
  width: "auto",
  whiteSpace: "nowrap",
};
const subTdStyle = {
  ...tdStyle,
  padding: "8px 10px",
  fontSize: "12px",
  width: "auto",
  whiteSpace: "nowrap",
};

const StatusBadge = ({ status }) => {
  const key = String(status || "").toLowerCase();
  const style = STATUS_STYLES[key] || STATUS_STYLES.default;
  const label = status
    ? String(status).charAt(0).toUpperCase() + String(status).slice(1).toLowerCase()
    : "-";
 
  return (
    <span
      style={{
        ...style,
        padding: "2px 6px",
        borderRadius: 4,
        display: "inline-block",
        minWidth: 70,
        fontSize: "11px",
        lineHeight: 1.2,
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.15)",
      }}
    >
      {label} 
    </span>
  );
};

const DisabledEditButton = ({ tooltip = "Complaint Closed" }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Tooltip title={tooltip} placement="top">
      <span
        style={{ display: "inline-block", cursor: "not-allowed" }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <button
          type="button"
          className="btn btn-link p-1"
          disabled
          style={{
            pointerEvents: "none",
            opacity: isHovered ? 1 : 0.6,
            color: isHovered ? "#dc2626" : "#6c757d",
          }}
        >
          <FontAwesomeIcon icon={isHovered ? faBan : faEdit} />
        </button>
      </span>
    </Tooltip>
  );
};

const ExpandButton = ({ isExpanded, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={isExpanded ? "Collapse row" : "Expand row"}
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 20,
      height: 20,
      borderRadius: 4,
      border: `1px solid ${isExpanded ? ORANGE : "#D1D5DB"}`,
      backgroundColor: "#FFFFFF",
      color: isExpanded ? ORANGE : "#6B7280",
      fontSize: "16px",
      fontWeight: 500,
      cursor: "pointer",
      lineHeight: 1,
    }}
  >
    {isExpanded ? "−" : "+"}
  </button>
);

const InnerTable = ({ assignments = [], isLoading }) => {
  if (isLoading) {
    return <TableLoader rows={4} columns={8} height={180} />;
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <table
        style={{
          width: "auto",
          borderCollapse: "collapse",
          textAlign: "center",
          fontSize: "12px",
          tableLayout: "auto",
        }}
      >
        <thead>
          <tr>
            {INNER_COLUMNS.map((col) => (
              <th key={col} style={subThStyle}>
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {!assignments.length ? (
            <tr>
              <td colSpan={INNER_COLUMNS.length} style={subTdStyle}>
                No data
              </td>
            </tr>
          ) : (
            assignments.map((item, index) => (
              <tr key={`${item.assignedTo}-${index}`}>
                <td style={subTdStyle}>{index + 1}</td>
                <td style={subTdStyle}>{item.assignedTo && item.assignedTo !== "-" ? item.assignedTo : "N/A"}</td>
                <td style={subTdStyle}>
                  <StatusBadge status={item.status} />
                </td>
                <td style={{ ...subTdStyle, whiteSpace: "normal", maxWidth: 220 }}>
                  {item.actionRemarks && item.actionRemarks !== "-" ? item.actionRemarks : "N/A"}
                </td>
                <td style={subTdStyle}>{formatDateTime(item.assignedOn)}</td>
                <td style={subTdStyle}>{formatDateTime(item.actionTakenOn)}</td>
                <td style={subTdStyle}>{item.totalTime && item.totalTime !== "-" ? item.totalTime : "N/A"}</td>
                <td style={subTdStyle}>{item.assignedBy && item.assignedBy !== "-" ? item.assignedBy : "N/A"}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

const DrainSlitComplaintTable = () => {
  const {
    openAssignComplaint,
    openEditComplaint,
    drainSlitComplaintOuterReport,
    isLoadingDrainSlitComplaintOuterReport,
    pageNumber,
    pageSize,
    totalRecords,
    totalPages,
    fetchDrainSlitComplaintOuterReport,
    complaintHistory,
    isLoadingComplaintHistory,
    fetchDrainSlitComplaintHistory,
  } = useDrainSlitComplaintReportStore();

  const [expandedIds, setExpandedIds] = useState([]);
  const [previewImage, setPreviewImage] = useState("");

  const rows = drainSlitComplaintOuterReport || [];
  const rangeStart = totalRecords ? (pageNumber - 1) * pageSize + 1 : 0;
  const rangeEnd = Math.min(pageNumber * pageSize, totalRecords);

  const pageNumbers = [];
  for (let i = 1; i <= Math.min(totalPages || 1, 6); i += 1) pageNumbers.push(i);

  const toggleExpand = (complaintId) => {
    const isOpen = expandedIds.includes(complaintId);
    if (isOpen) {
      setExpandedIds((prev) => prev.filter((id) => id !== complaintId));
      return;
    }
    setExpandedIds((prev) => [...prev, complaintId]);
    fetchDrainSlitComplaintHistory(complaintId);
  };

  const changePage = (nextPage) => {
    fetchDrainSlitComplaintOuterReport({ PageNumber: nextPage, PageSize: pageSize });
  };

  const changePageSize = (nextSize) => {
    fetchDrainSlitComplaintOuterReport({ PageNumber: 1, PageSize: nextSize });
  };

  return (
    <div>
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ background: "#D7D7D7", borderRadius: "8px 8px 0 0" }}
      >
        <h6 className="mt-3" style={{ fontSize: "16px", fontWeight: 700, color: "#231F20" }}>
          Drain Slit Complaints
        </h6>
      </div>

      {isLoadingDrainSlitComplaintOuterReport ? (
        <TableLoader height={500} columns={10} />
      ) : (
        <>
          <div style={{ overflow: "hidden", backgroundColor: "#FFFFFF" }}>
            <div style={{ maxHeight: 500, overflow: "auto" }}>
              <table
                style={{
                  minWidth: 3200,
                  width: "100%",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  textAlign: "center",
                  fontSize: "12px",
                }}
              >
                <thead>
                  <tr>
                    <th style={{ ...thStyle, width: 48 }} />
                    {MAIN_COLUMNS.map((col) => (
                      <th key={col} style={thStyle}>
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={MAIN_COLUMNS.length + 1} style={tdStyle}>
                        No records found.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, rowIndex) => {
                      const serialNo = (pageNumber - 1) * pageSize + rowIndex + 1;
                      const isExpanded = expandedIds.includes(row.complaintId);

                      return (
                        <Fragment key={row.complaintId}>
                          <tr>
                            <td style={tdStyle}>
                              <ExpandButton
                                isExpanded={isExpanded}
                                onClick={() => toggleExpand(row.complaintId)}
                              />
                            </td>
                            <td style={tdStyle}>{serialNo}</td>
                            <td style={tdStyle}>
                              {row.imageUrl ? (
                                <img
                                  src={row.imageUrl}
                                  alt="Complaint"
                                  onClick={() => setPreviewImage(row.imageUrl)}
                                  style={{
                                    width: 28,
                                    height: 28,
                                    objectFit: "cover",
                                    borderRadius: 4,
                                    display: "block",
                                    margin: "0 auto",
                                    cursor: "pointer",
                                  }}
                                />
                              ) : (
                                "N/A"
                              )}
                            </td>
                            <td style={tdStyle}>{row.complaintNo || "N/A"}</td>
                            <td style={tdStyle}>{row.contactNo || "N/A"}</td>
                            <td style={tdStyle}>{row.complainantName || "N/A"}</td>
                            <td style={tdStyle}>{row.wardName || "N/A"}</td>
                            <td style={tdStyle}>{row.colonyName || "N/A"}</td>
                            <td style={tdStyle}>{row.description || "N/A"}</td>
                            <td style={tdStyle}>
                              {row.latitude != null && row.longitude != null
                                ? `${row.latitude}, ${row.longitude}`
                                : "N/A"}
                            </td>
                            <td style={tdStyle}>{(row.complaintDateFormatted)}</td>
                            <td style={tdStyle}>{(row.closedDateFormatted)}</td>
                            <td style={tdStyle}>
                              <StatusBadge status={row.status} />
                            </td>
                            <td style={tdStyle}>{row.actualDuration && row.actualDuration !== "-" ? row.actualDuration : "N/A"}</td>
                            <td style={tdStyle}>
                              {row.closeLatitude != null && row.closeLongitude != null
                                ? `${row.closeLatitude}, ${row.closeLongitude}`
                                : "N/A"}
                            </td>
                            <td style={tdStyle}>
                              {row.closingImageUrl ? (
                                <img
                                  src={row.closingImageUrl}
                                  alt="Closing"
                                  onClick={() => setPreviewImage(row.closingImageUrl)}
                                  style={{
                                    width: 28,
                                    height: 28,
                                    objectFit: "cover",
                                    borderRadius: 4,
                                    display: "block",
                                    margin: "0 auto",
                                    cursor: "pointer",
                                  }}
                                />
                              ) : (
                                "N/A"
                              )}
                            </td>
                            <td style={tdStyle}>{row.actionRemarks && row.actionRemarks !== "-" ? row.actionRemarks : "N/A"}</td>
                            <td style={tdStyle}>{row.revisedWardName && row.revisedWardName !== "-" ? row.revisedWardName : "N/A"}</td>
                            <td style={tdStyle}>{row.revisedColonyName && row.revisedColonyName !== "-" ? row.revisedColonyName : "N/A"}</td>
                            <td style={tdStyle}>
                              {row.revisedLatitude != null && row.revisedLongitude != null
                                ? `${row.revisedLatitude}, ${row.revisedLongitude}`
                                : "N/A"}
                            </td>
                            <td style={tdStyle}>{row.closedBy && row.closedBy !== "-" ? row.closedBy : "N/A"}</td>
                            <td style={tdStyle}>
                              {String(row.status).toLowerCase() === "closed" ? (
                                <DisabledEditButton tooltip="Complaint Closed" />
                              ) : String(row.status).toLowerCase() === "invalid" ? (
                                <DisabledEditButton tooltip="Complaint Invalid" />
                              ) : String(row.status).toLowerCase() === "opened" ? (
                                <Tooltip title="Assign Complaint To" placement="top">
                                  <button
                                    type="button"
                                    className="btn btn-link p-1 text-primary"
                                    onClick={() => openAssignComplaint(row)}
                                  >
                                    <FontAwesomeIcon icon={faTruck} />
                                  </button>
                                </Tooltip>
                              ) : (
                                <Tooltip title="Edit Complaint Details" placement="top">
                                  <button
                                    type="button"
                                    className="btn btn-link p-1 text-primary"
                                    onClick={() => openEditComplaint(row)}
                                  >
                                    <FontAwesomeIcon icon={faEdit} />
                                  </button>
                                </Tooltip>
                              )}
                            </td>
                          </tr>

                          {isExpanded && (
                            <tr style={{ backgroundColor: EXPAND_BG }}>
                              <td
                                colSpan={MAIN_COLUMNS.length + 1}
                                style={{ border: `1px solid ${BORDER}`, padding: "16px 20px" }}
                              >
                                <InnerTable
                                  assignments={complaintHistory[row.complaintId] || []}
                                  isLoading={isLoadingComplaintHistory}
                                />
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div
            style={{
              marginTop: 16,
              paddingTop: 12,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              fontSize: "14px",
              color: "#6B7280",
            }}
          >
            <span>
              Showing {rangeStart}-{rangeEnd} of {totalRecords} results.
            </span>

            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, margin: 0 }}>
                Rows per page
                <select
                  value={pageSize}
                  onChange={(e) => changePageSize(Number(e.target.value))}
                  className="form-select form-select-sm"
                  style={{ width: 70 }}
                >
                  {[5, 10, 25].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>

              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <button
                  type="button"
                  disabled={pageNumber === 1}
                  onClick={() => changePage(pageNumber - 1)}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 6,
                    border: `1px solid ${HEADER_BG}`,
                    backgroundColor: "#FFFFFF",
                    color: HEADER_BG,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: pageNumber === 1 ? "not-allowed" : "pointer",
                    opacity: pageNumber === 1 ? 0.5 : 1,
                  }}
                >
                  <FiChevronLeft />
                </button>
                {pageNumbers.map((num) => {
                  const isActive = pageNumber === num;
                  return (
                    <button
                      key={num}
                      type="button"
                      onClick={() => changePage(num)}
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 6,
                        border: `1px solid ${HEADER_BG}`,
                        backgroundColor: isActive ? HEADER_BG : "#FFFFFF",
                        color: isActive ? "#FFFFFF" : HEADER_BG,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    >
                      {num}
                    </button>
                  );
                })}
                <button
                  type="button"
                  disabled={pageNumber >= totalPages}
                  onClick={() => changePage(pageNumber + 1)}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 6,
                    border: `1px solid ${HEADER_BG}`,
                    backgroundColor: "#FFFFFF",
                    color: HEADER_BG,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: pageNumber >= totalPages ? "not-allowed" : "pointer",
                    opacity: pageNumber >= totalPages ? 0.5 : 1,
                  }}
                >
                  <FiChevronRight />
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <Modal show={!!previewImage} onHide={() => setPreviewImage("")} centered>
        <Modal.Header closeButton>
          <Modal.Title>Image Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <img
            src={previewImage}
            alt="Preview"
            style={{ width: "100%", maxHeight: 400, objectFit: "contain", borderRadius: 5 }}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default DrainSlitComplaintTable;
